/**
 * Human duration from milliseconds — `1h 3m`, `1m 3s`, `3s`.
 *
 * Seconds are deliberately dropped once hours are present (`1h 2m 45s` reads
 * `1h 2m`). That is the behaviour of the CompactStats card this came from,
 * kept so the two surfaces agree; pinned by a test so it is not mistaken for
 * a rounding bug later.
 *
 * Extracted verbatim from CompactStats, which owned the only implementation
 * with this contract, so the Compare table and the Overview compact stats
 * agree by construction rather than by coincidence (TELV1-581).
 *
 * Deliberately NOT reused elsewhere:
 * - `Statistics.tsx` has a same-named helper with a *different* contract: it
 *   returns padded `{hours, minutes, seconds}` strings feeding three animated
 *   CountUp components. Swapping it would delete the animation and change the
 *   rendered format on two live screens (Overview and ProjectReport). That is
 *   a design change, not a refactor.
 * - `TranscriptPanel.formatDuration` takes two ISO timestamps and renders
 *   `< 1 min` / `N min`.
 * - The txt export formats its own `0h:01m:03s`.
 *
 * So this unifies two of the four renderings, not all of them. Unifying the
 * rest is a separate piece of work.
 */
export const formatDurationMs = (ms: number): string => {
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
};

/**
 * Signed duration, for a comparison delta: `+1m 3s`, `-45s`, `—` for no
 * change.
 *
 * Without this a millisecond-valued delta renders as a raw `+63000` — which
 * is why switching the comparison to milliseconds needs a delta formatter and
 * not just a value formatter.
 */
export const formatDurationDeltaMs = (ms: number): string => {
  // Guard NaN explicitly. `!ms` alone is also falsy for NaN, which would
  // render a non-numeric delta as "no change" — a wrong answer dressed up as
  // a real one. An em-dash is right for zero, not for garbage. PR review.
  if (!Number.isFinite(ms)) return '';
  if (ms === 0) return '—';
  return `${ms > 0 ? '+' : '-'}${formatDurationMs(Math.abs(ms))}`;
};
